import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { ProfileCard } from "../../ProfileCard";
import routes from "../../../constants/routes";
import { Icon } from "../../icons/Icon";
import TopBar from "./TopBar";
import MenuGroup from "./MenuGroup";
import { User, Role } from "../../../constants/interfaces";
import transparentize from "../../../utils/transparentize";
import hasUserRole from "../../../utils/hasUserRole";

const styles = (theme: Theme) => {
  return createStyles({
    expansionPanel: {
      flexDirection: "column"
    },
    link: {
      display: "block",
      textDecoration: "none",
      width: "300px"
    },
    linkIcon: {
      color: theme.palette.primary.light,
      margin: 0
    },
    linkWrapper: {
      padding: theme.spacing(1)
    },
    profileWrapper: {
      padding: theme.spacing(2),
      boxShadow: theme.shadows[2]
    },
    nested: {
      paddingLeft: theme.spacing(4)
    },
    background: {
      backgroundColor: transparentize(theme.palette.primary.main, 0.3)
    }
  });
};

type MenuLink = {
  external?: boolean;
  label: string;
  icon: string;
  url: string;
  classes?: any;
};

const topLinks = (user: User): MenuLink[] => {
  const links = [
    {
      label: routes.HOME.label,
      url: routes.HOME.url,
      icon: "home"
    },
    {
      label: routes.MY_DETAILS.label,
      url: routes.MY_DETAILS.url,
      icon: "person"
    }
  ];
  hasUserRole(user, Role.LEGAL_ENTITIES) &&
    links.push({
      label: routes.MY_LEGAL_ENTITIES.label,
      url: routes.MY_LEGAL_ENTITIES.url,
      icon: "account_balance"
    });
  hasUserRole(user, Role.PREMISES) &&
    links.push({
      label: routes.MY_PREMISES.label,
      url: routes.MY_PREMISES.url,
      icon: "business"
    });
  links.push({
    label: routes.CONTACT.label,
    url: routes.CONTACT.url,
    icon: "chat"
  });
  return links;
};
const auditLinks = (user: User): MenuLink[] => {
  const links = [
    {
      label: routes.AUDIT_HISTORY.label,
      url: routes.AUDIT_HISTORY.url,
      icon: "assignment"
    }
  ];
  hasUserRole(user, Role.MCOLS) &&
    links.push({
      label: routes.CAM_LIST.label,
      url: routes.CAM_LIST.url,
      icon: "list"
    });
  return links;
};

const dhbLinks: MenuLink[] = [
  {
    label: routes.AUDIT_STATUS_REPORT.label,
    url: routes.AUDIT_STATUS_REPORT.url,
    icon: "description"
  }
];
const registersLinks = (user: User): MenuLink[] => {
  const links = [
    {
      external: true,
      label: routes.PHARMACY_LICENSE_REGISTER.label,
      url: routes.PHARMACY_LICENSE_REGISTER.url,
      icon: "open_in_new"
    }
  ];
  hasUserRole(user, Role.VERIFY_RI) &&
    links.push({
      external: false,
      label: routes.VERIFY_RI.label,
      url: routes.VERIFY_RI.url,
      icon: "done"
    });
  hasUserRole(user, Role.RESTRICTION_NOTICE) &&
    links.push({
      external: false,
      label: routes.SEARCH_RESTRICTION_NOTICE.label,
      url: routes.SEARCH_RESTRICTION_NOTICE.url,
      icon: "person"
    });

  return links;
};
const regulatoryInstrumentLinks: MenuLink[] = [
  {
    label: routes.REGULATORY_INSTRUMENTS.label,
    url: routes.REGULATORY_INSTRUMENTS.url,
    icon: "gavel"
  },
  {
    label: routes.CONTROLLED_DRUGS.label,
    url: routes.CONTROLLED_DRUGS.url,
    icon: "report"
  },
  {
    label: routes.IMPORT_EXPORT_LIST.label,
    url: routes.IMPORT_EXPORT_LIST.url,
    icon: "swap_horiz"
  }
];
const bottomLinks: MenuLink[] = [
  {
    label: routes.RESOURCES.label,
    url: routes.RESOURCES.url,
    icon: "description"
  },
  { label: routes.SUPPORT.label, url: routes.SUPPORT.url, icon: "help" },
  { label: routes.TERMS.label, url: routes.TERMS.url, icon: "info" },
  { label: routes.PRIVACY.label, url: routes.PRIVACY.url, icon: "security" }
];

type Props = {
  classes: any;
  user: User;
};

type State = {
  drawerOpen: boolean;
};

const Content = ({
  icon,
  label,
  iconClass,
  onClick
}: {
  icon: string;
  label: string;
  iconClass: string;
  onClick?: Function;
}) => {
  const itemProps = {};
  if (onClick) Object.assign(itemProps, { onClick });
  return (
    <ListItem button {...itemProps}>
      <ListItemIcon className={iconClass}>
        <Icon name={icon} />
      </ListItemIcon>
      <ListItemText
        primaryTypographyProps={{
          color: "textPrimary",
          variant: "body2"
        }}
        primary={label}
      />
    </ListItem>
  );
};

class Header extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      drawerOpen: false
    };
    this.toggleDrawer = this.toggleDrawer.bind(this);
  }

  toggleDrawer() {
    this.setState(prevState => ({
      drawerOpen: !prevState.drawerOpen
    }));
  }

  renderLink(link: MenuLink) {
    const { url, label, icon, external } = link;
    const classes = this.props.classes;
    return external ? (
      <a
        className={classes.link}
        href={url}
        key={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        <Content label={label} icon={icon} iconClass={classes.linkIcon} />
      </a>
    ) : (
      <Link
        data-test-control={label}
        key={url}
        to={url}
        onClick={this.toggleDrawer}
        className={classes.link}
      >
        <Content label={label} icon={icon} iconClass={classes.linkIcon} />
      </Link>
    );
  }

  render() {
    const { classes, user } = this.props;
    const { drawerOpen } = this.state;
    return (
      <div>
        <TopBar user={user} onMenuPress={this.toggleDrawer} />
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={this.toggleDrawer}
          ModalProps={{
            BackdropProps: {
              classes: {
                root: classes.background
              }
            }
          }}
        >
          <div className={classes.profileWrapper}>
            <ProfileCard
              user={user}
              onLogoutClick={this.toggleDrawer}
              onProfileClick={this.toggleDrawer}
            />
          </div>
          <List className={classes.linkWrapper}>
            {topLinks(user).map(link => this.renderLink(link))}
            <MenuGroup label="Regulatory Instruments">
              {regulatoryInstrumentLinks.map(link => this.renderLink(link))}
            </MenuGroup>
            <MenuGroup label="Audits">
              {auditLinks(user).map(link => this.renderLink(link))}
            </MenuGroup>
            <MenuGroup label="Registers">
              {registersLinks(user).map(link => this.renderLink(link))}
            </MenuGroup>
            {hasUserRole(user, Role.DHB) && (
              <MenuGroup label="DHB Access">
                {dhbLinks.map(link => this.renderLink(link))}
              </MenuGroup>
            )}
            <MenuGroup label="Resources">
              {bottomLinks.map(link => this.renderLink(link))}
            </MenuGroup>
          </List>
        </Drawer>
      </div>
    );
  }
}

const mapStateToProps = ({ user }: any) => ({
  user: user.user
});

export const styled = withStyles(styles)(Header);
const connected = connect(mapStateToProps)(styled);
export { connected as Header };
